import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Ban, Flag, Image as ImageIcon, ShieldCheck } from "lucide-react";

import { api } from "@/lib/api";
import { REPORT_REASONS } from "@/lib/constants";
import { SAFETY_KEYS } from "@/hooks/use-save-toggle";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { Button, ChipGroup } from "@/components/ui";

const LABEL_TO_KEY = Object.fromEntries(Object.entries(REPORT_REASONS).map(([k, v]) => [v, k]));

/** Report / block actions for a member (profile detail, received requests, connections). */
export function MemberActionsDialog({ member, photoId, onClose, onBlocked, extra }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [mode, setMode] = useState("menu");
  const [reason, setReason] = useState([]);
  const [details, setDetails] = useState("");
  const [photoReport, setPhotoReport] = useState(false);
  const [busy, setBusy] = useState(false);

  const close = () => { setMode("menu"); setReason([]); setDetails(""); setPhotoReport(false); onClose(); };

  const submitReport = async () => {
    if (!member || !reason.length) return toast("Choose a reason", "error");
    setBusy(true);
    try {
      await api("/reports", { method: "POST", body: { member_id: member.id, reason: LABEL_TO_KEY[reason[0]], details: details || null, photo_id: photoReport ? photoId : null } });
      setMode("reported");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const block = async () => {
    setBusy(true);
    try {
      await api("/blocks", { method: "POST", body: { member_id: member.id } });
      toast(`${member.display_name} has been blocked`, "success");
      SAFETY_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      close();
      onBlocked?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "report" ? (photoReport ? "Report this photo" : `Report ${member?.display_name}`) : mode === "block" ? `Block ${member?.display_name}?` : mode === "reported" ? "Report received" : member?.display_name;
  return (
    <Modal open={!!member} onClose={close} title={title ?? ""} testId="member-actions-sheet">
      {mode === "menu" ? (
        <div className="flex flex-col gap-3">
          {extra}
          <Button testId="member-action-report" title="Report member" variant="outline" icon={Flag} onClick={() => setMode("report")} />
          {photoId ? <Button testId="member-action-report-photo" title="Report this photo" variant="outline" icon={ImageIcon} onClick={() => { setPhotoReport(true); setReason(["Inappropriate photo"]); setMode("report"); }} /> : null}
          <Button testId="member-action-block" title="Block member" variant="danger" icon={Ban} onClick={() => setMode("block")} />
        </div>
      ) : mode === "report" ? (
        <div>
          <p className="mb-2 text-sm font-bold">Reason</p>
          <ChipGroup testIdPrefix="report-reason" multi={false} options={Object.values(REPORT_REASONS)} value={reason} onChange={(v) => v.length && setReason(v)} />
          <label htmlFor="report-details" className="mb-2 mt-5 block text-sm font-bold">Details (optional)</label>
          <textarea id="report-details" data-testid="report-details-input" value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} placeholder="Tell us what happened"
            className="min-h-[100px] w-full rounded-xl border border-border p-3 text-[15px] focus:border-primary focus:outline-none" />
          <Button testId="report-submit-button" title="Submit report" loading={busy} className="mt-4 w-full" onClick={submitReport} />
        </div>
      ) : mode === "block" ? (
        <div>
          <p className="mb-6 text-sm leading-6">They won’t appear in your matches, can’t view your profile or send you requests. Pending requests are cancelled and any connection ends immediately, including WhatsApp access.</p>
          <div className="flex gap-3">
            <Button testId="block-cancel-button" title="Cancel" variant="outline" className="flex-1" onClick={close} />
            <Button testId="block-confirm-button" title="Block" variant="danger" loading={busy} className="flex-1" onClick={block} />
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-6 flex gap-3 rounded-xl bg-[#3A6C54]/10 p-4" data-testid="report-confirmation">
            <ShieldCheck className="h-[22px] w-[22px] shrink-0 text-[#3A6C54]" />
            <p className="text-sm">Thank you. Our safety team will review this report. You can also block this member at any time.</p>
          </div>
          <div className="flex gap-3">
            <Button testId="report-done-button" title="Done" variant="outline" className="flex-1" onClick={close} />
            <Button testId="report-block-button" title="Block too" variant="danger" className="flex-1" onClick={() => setMode("block")} />
          </div>
        </div>
      )}
    </Modal>
  );
}
